import { useMemo } from 'react';
import { MarketDisplayData } from 'src/components/MarketSelector/MarketSelectorTable/types';
import { ZERO } from 'src/constants/math';
import { marketHasInsuranceFund } from 'src/hooks/useInfoForCurrentlySelectedMarket';
import useDriftStore, { DriftStore } from 'src/stores/DriftStore/useDriftStore';
import { PerpMarketInfo } from 'src/stores/types';
import {
	ENUM_UTILS,
	MarketDetails24H,
	MarketId,
	UIMarket,
} from '@drift/common';
import {
	PerpMarketConfig,
	DriftClient,
	isVariant,
	BigNum,
	BASE_PRECISION_EXP,
	MarketType,
	MarketStatus,
} from '@drift-labs/sdk';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useFavouriteMarkets from 'src/hooks/useFavouriteMarkets';
import useHiddenPerpMarkets from 'src/hooks/useHiddenPerpMarkets';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useMarketsSortedByCriteria from 'src/hooks/useMarketsSortedByCriteria';
import { getMarketSelectorPricePercentChange } from 'src/components/MarketSelector/utils';
import { useAllPricingData } from 'src/hooks/query/pricing/usePricingData';
import { PricingData } from 'src/hooks/query/pricing/types';
import { pricingDataToMarkPrice } from 'src/hooks/useMarkPrice';

export default function useMarketSelectorPerpMarkets() {
	const perpMarketsLookup = usePerpMarketsLookup();
	const hiddenPerpMarkets = useHiddenPerpMarkets();

	const driftClient = useDriftStore((s) => s.driftClient.client);
	const marketsData = useDriftStore((s) => s.marketsData24H);
	const predictedFundings = useDriftStore((s) => s.predictedFundings);
	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const { favouriteMarketsIncludes } = useFavouriteMarkets();
	const { data: allPricingData } = useAllPricingData();

	const defaultSortedPerpMarkets = useMarketsSortedByCriteria({
		marketType: MarketType.PERP,
		sortBy: ['isFavorited', '24hVolume'],
	});

	// Using a ref here because `predictedFundings` updates non-stop.
	return useMemo(() => {
		if (!driftClient) {
			return [];
		}

		const mappedMarkets = defaultSortedPerpMarkets
			// Don't show prediction markets.
			.filter((mktSortingInfo) => {
				return !UIMarket.checkIsPredictionMarket(
					perpMarketsLookup[mktSortingInfo.marketIndex]
				);
			})
			// Don't show hidden low-activity perp markets.
			.filter((mktSortingInfo) => {
				return !hiddenPerpMarkets.has(mktSortingInfo.marketIndex);
			})
			.map((mktSortingInfo) => {
				return {
					marketInfo: getMarketInfoByIndexAndType(
						mktSortingInfo.marketIndex,
						mktSortingInfo.marketType
					) as PerpMarketInfo,
					marketConfig: perpMarketsLookup[mktSortingInfo.marketIndex],
					isHot: mktSortingInfo.isHot,
				};
			});

		return getPerpMarketsRenderingInfo(
			mappedMarkets,
			driftClient,
			marketsData,
			predictedFundings,
			favouriteMarketsIncludes,
			allPricingData
		).filter((mkt) => {
			return (
				!ENUM_UTILS.match(
					mkt.marketAccount?.status,
					MarketStatus.REDUCE_ONLY
				) &&
				!ENUM_UTILS.match(mkt.marketAccount?.status, MarketStatus.SETTLEMENT)
			);
		});
	}, [
		driftClient,
		defaultSortedPerpMarkets,
		marketsData,
		predictedFundings,
		favouriteMarketsIncludes,
		getMarketInfoByIndexAndType,
		perpMarketsLookup,
		allPricingData,
		hiddenPerpMarkets,
	]);
}

const getPerpMarketsRenderingInfo = (
	defaultSortedPerpMarkets: {
		marketInfo: PerpMarketInfo;
		marketConfig: PerpMarketConfig;
		isHot: boolean;
	}[],
	driftClient: DriftClient,
	marketsData: MarketDetails24H[],
	predictedFundings: DriftStore['predictedFundings'],
	favouriteMarketsIncludes: (marketId: MarketId) => boolean,
	allPricingData?: Map<string, PricingData>
): MarketDisplayData[] => {
	return defaultSortedPerpMarkets.map<MarketDisplayData>((mktInfo) => {
		const marketId = MarketId.createPerpMarket(
			mktInfo.marketConfig.marketIndex
		);

		if (!mktInfo.marketInfo) {
			const marketConfig = mktInfo.marketConfig;

			// Market Info Hasn't Loaded Yet .. Return default/blank values
			return {
				symbol: marketConfig.symbol,
				baseAssetSymbol: marketConfig.baseAssetSymbol,
				market: UIMarket.createPerpMarket(marketConfig.marketIndex),
				price: null,
				pricePercentChange: null,
				price24hAgo: null,
				volume: null,
				high: null,
				low: null,
				openInterest: undefined,
				funding: undefined,
				funding24H: undefined,
				launchTs: 0,
				config: marketConfig,
				noInsuranceFund: false,
				marketAccount: undefined,
				isHot: false,
				isFavorite: favouriteMarketsIncludes(marketId),
			};
		}

		const mkt = mktInfo.marketInfo.config;

		const perpMarketAccount = driftClient.getPerpMarketAccount(mkt.marketIndex);
		const uiMarket = UIMarket.createPerpMarket(mkt.marketIndex);
		const market24h = marketsData.find(
			(mktData) =>
				mktData.marketIndex === uiMarket.marketIndex &&
				isVariant(mktData.marketType, 'perp')
		);
		const markPrice = pricingDataToMarkPrice(allPricingData?.get(marketId.key));
		const baseAmount = (perpMarketAccount?.amm.baseAssetAmountLong ?? ZERO).add(
			perpMarketAccount?.amm.baseAssetAmountShort.abs() ?? ZERO
		);

		const openInterest =
			baseAmount && markPrice
				? BigNum.from(baseAmount, BASE_PRECISION_EXP).mul(markPrice).toNum()
				: undefined;

		const noInsuranceFund = !marketHasInsuranceFund(perpMarketAccount);

		const avgLongFunding = market24h?.avgLongFunding ?? 0;
		const avgShortFunding = market24h?.avgShortFunding ?? 0;

		const funding24H =
			Math.abs(avgLongFunding) >= Math.abs(avgShortFunding)
				? avgLongFunding
				: avgShortFunding;

		return {
			symbol: mkt.symbol,
			baseAssetSymbol: mkt.baseAssetSymbol,
			market: uiMarket,
			price: markPrice,
			pricePercentChange: getMarketSelectorPricePercentChange(
				markPrice.toNum(),
				market24h?.price24hAgo ?? 0
			),
			price24hAgo: market24h?.price24hAgo ?? 0,
			volume: market24h?.quoteVolume ?? 0,
			high: market24h?.priceHigh ?? null,
			low: market24h?.priceLow ?? null,
			openInterest,
			funding: parseFloat(
				predictedFundings
					.find((funding) => funding.marketIndex === mkt.marketIndex)
					?.fundingRate.toFixed(5) ?? '0'
			),
			funding24H,
			launchTs: mkt.launchTs,
			config: mkt,
			noInsuranceFund,
			marketAccount: mktInfo.marketInfo.account,
			isHot: mktInfo.isHot,
			isFavorite: favouriteMarketsIncludes(marketId),
		};
	});
};
